import { getSession, clearSession } from './session.js';
import { renderBusinessRegister } from './components/BusinessRegister.js';
import { renderBusinessPending } from './components/BusinessPending.js';
import { renderAdminPanel } from './components/AdminPanel.js';
import { renderDashboard } from './components/Dashboard.js';
import { renderNewSession } from './components/NewSession.js';
import { renderSessionDetail } from './components/SessionDetail.js';
import { renderVerify } from './components/Verify.js';
import { renderSettings } from './components/Settings.js';
import { renderTemplateLibrary } from './components/TemplateLibrary.js';
import { renderTemplateBuilder } from './components/TemplateBuilder.js';
import { renderDocumentFiller } from './components/DocumentFiller.js';
import { renderRestaurantMenu } from './components/RestaurantMenu.js';
import { renderRestaurantTablet } from './components/RestaurantTablet.js';
import { renderKitchenDisplay } from './components/KitchenDisplay.js';
import { renderRestaurantTables } from './components/RestaurantTables.js';
import { renderBalanceChecker } from './components/BalanceChecker.js';
import { renderCustomerCRM } from './components/CustomerCRM.js';
import { renderCompliance } from './components/Compliance.js';
import { renderEmployeeManagement } from './components/EmployeePIN.js';
import { renderBusinessAnalytics } from './components/BusinessAnalytics.js';
import { renderDocumentScanner } from './components/DocumentScanner.js';
import { renderConstructionInvoices } from './components/ConstructionInvoices.js';
import { renderPayroll } from './components/Payroll.js';
import { renderRestaurantSchedule } from './components/RestaurantSchedule.js';
import { applyTheme, applyLogoToSidebar } from './components/Settings.js';
import { renderAccountHold } from './components/AccountHold.js';
import { renderIdentityVerification } from './components/IdentityVerification.js';
import { renderLandingPage } from './components/LandingPage.js';
import { renderBilling } from './components/Billing.js';

/** True when running as a browser web app (not Electron). */
function isWebMode(): boolean {
  return typeof (window as unknown as { preuve?: unknown }).preuve === 'undefined';
}

const FULLSCREEN_ROUTES = new Set(['restaurant-tablet', 'kitchen']);

let currentRoute = 'login';
let currentParams: Record<string, string> = {};
let mainContentEl: HTMLElement;
let shellIsFullscreen = false;
let shellSessionKey = '';  // tracks who built the current shell

function sessionKey(): string {
  const s = getSession();
  return s ? `${s.businessId}:${s.industry}:${s.isAdmin}` : '';
}

export function navigate(route: string, params: Record<string, string> = {}): void {
  const willFullscreen = FULLSCREEN_ROUTES.has(route);
  const sessionChanged = sessionKey() !== shellSessionKey;
  // Always rebuild the shell for unauthenticated web visitors so the back button
  // correctly appears/disappears when navigating between landing and inner pages
  const webPublicTransition = isWebMode() && !getSession();
  const needsRebuild = willFullscreen !== shellIsFullscreen || sessionChanged || webPublicTransition;
  currentRoute = route;
  currentParams = params;
  if (needsRebuild) {
    shellIsFullscreen = willFullscreen;
    buildShell();
  } else {
    renderRoute();
    updateActiveNav();
  }
}

function renderRoute(): void {
  if (!mainContentEl) return;
  mainContentEl.innerHTML = '';

  const session = getSession();

  const publicRoutes = ['login', 'pending', 'verify', 'account-hold', 'landing'];
  if (!publicRoutes.includes(currentRoute) && !session) {
    // Web visitors who aren't logged in see the landing page
    if (isWebMode()) {
      renderLandingPage(mainContentEl, navigate);
    } else {
      renderBusinessRegister(mainContentEl, navigate);
    }
    return;
  }

  switch (currentRoute) {
    case 'landing':    renderLandingPage(mainContentEl, navigate); break;
    case 'login':      renderBusinessRegister(mainContentEl, navigate); break;
    case 'pending':    renderBusinessPending(mainContentEl, currentParams, navigate); break;
    case 'admin':      renderAdminPanel(mainContentEl, navigate); break;
    case 'dashboard':  renderDashboard(mainContentEl, (r) => navigate(r.name, r as Record<string, string>)); break;
    case 'new-session': renderNewSession(mainContentEl, (r) => navigate(r.name, r as Record<string, string>)); break;
    case 'session':    renderSessionDetail(mainContentEl, currentParams['id'] ?? '', (r) => navigate(r.name, r as Record<string, string>)); break;
    case 'verify':     renderVerify(mainContentEl); break;
    case 'settings':   renderSettings(mainContentEl); break;
    case 'templates':  renderTemplateLibrary(mainContentEl, navigate); break;
    case 'template-builder': renderTemplateBuilder(mainContentEl, navigate, currentParams); break;
    case 'fill-document':    renderDocumentFiller(mainContentEl, navigate, currentParams); break;
    case 'restaurant':        renderRestaurantMenu(mainContentEl, navigate); break;
    case 'restaurant-tablet': renderRestaurantTablet(mainContentEl, navigate, currentParams); break;
    case 'kitchen':           renderKitchenDisplay(mainContentEl, navigate); break;
    case 'tables':            renderRestaurantTables(mainContentEl, navigate); break;
    case 'schedule':          renderRestaurantSchedule(mainContentEl, navigate); break;
    case 'balance':           renderBalanceChecker(mainContentEl, navigate); break;
    case 'customers':         renderCustomerCRM(mainContentEl, navigate); break;
    case 'compliance':        renderCompliance(mainContentEl, navigate); break;
    case 'employees':         renderEmployeeManagement(mainContentEl, navigate); break;
    case 'analytics':         renderBusinessAnalytics(mainContentEl, navigate); break;
    case 'scanner':           renderDocumentScanner(mainContentEl, navigate); break;
    case 'invoices':          renderConstructionInvoices(mainContentEl, navigate); break;
    case 'payroll':           renderPayroll(mainContentEl, navigate); break;
    case 'account-hold': {
      const holdBizId   = currentParams['businessId'] ?? session?.businessId ?? '';
      const holdBizName = currentParams['businessName'] ?? session?.businessName ?? '';
      renderAccountHold(mainContentEl, holdBizId, holdBizName, navigate);
      break;
    }
    case 'verify-identity': {
      const s = getSession();
      renderIdentityVerification(mainContentEl, s?.businessId ?? '');
      break;
    }
    case 'billing':    renderBilling(mainContentEl, navigate); break;
    default:           renderDashboard(mainContentEl, (r) => navigate(r.name, r as Record<string, string>));
  }
}

function updateActiveNav(): void {
  document.querySelectorAll('.nav-item[data-route]').forEach((el) => {
    el.classList.toggle('active', el.getAttribute('data-route') === currentRoute);
  });
}

function buildShell(): void {
  const app = document.getElementById('app')!;
  const session = getSession();
  shellSessionKey = sessionKey();

  if (shellIsFullscreen) {
    app.innerHTML = `
      <div id="main-content" style="height:100vh;overflow:hidden"></div>
      <div class="toast-container" id="toast-container"></div>
    `;
    mainContentEl = document.getElementById('main-content')!;
    renderRoute();
    return;
  }

  // Web mode + unauthenticated OR landing page: no sidebar, no titlebar, fully scrollable
  // Show a back-to-home header on inner pages (login, verify, etc.) but NOT on the landing page itself
  if (isWebMode() && (!session || currentRoute === 'landing')) {
    const isLanding = currentRoute === 'landing';
    app.innerHTML = `
      ${isLanding ? '' : `
        <div style="background:#0f172a;padding:12px 20px;display:flex;align-items:center;border-bottom:1px solid rgba(255,255,255,.08)">
          <button id="web-back-home" style="background:none;border:none;color:#94a3b8;font-size:13px;cursor:pointer;padding:0;display:flex;align-items:center;gap:6px">
            <span style="font-size:16px">←</span> <span style="font-weight:900;color:#d4a017">✦ PREUVU</span>
          </button>
        </div>
      `}
      <div id="main-content"></div>
      <div class="toast-container" id="toast-container"></div>
    `;
    mainContentEl = document.getElementById('main-content')!;
    document.getElementById('web-back-home')?.addEventListener('click', () => navigate('landing'));
    renderRoute();
    return;
  }

  const isAdmin = session?.isAdmin === true;
  const industry = session?.industry ?? 'general';

  app.innerHTML = `
    <div class="titlebar">
      <span class="titlebar-title">
        <span class="titlebar-brand">PREUVU</span> &nbsp;·&nbsp; Digital Notary
      </span>
      ${session ? `<span style="font-size:11px;color:var(--text-muted);margin-left:auto;-webkit-app-region:no-drag">${esc(session.businessName)}</span>` : ''}
    </div>
    <div class="mobile-header">
      <button class="mobile-hamburger" id="hamburger-btn" aria-label="Open menu">&#9776;</button>
      <span class="mobile-brand"><span class="m-gold">PREUVU</span></span>
      ${session ? `<span class="mobile-biz">${esc(session.businessName)}</span>` : ''}
    </div>
    <div class="sidebar-overlay" id="sidebar-overlay"></div>
    <div class="layout">
      <nav class="sidebar" id="sidebar">
        <div class="sidebar-close-wrap">
          <button class="sidebar-close-btn-el" id="sidebar-close-btn" aria-label="Close menu"><i class="bi bi-x-lg"></i></button>
        </div>
        <div class="sidebar-logo" id="sidebar-logo-wrap">
          <div class="sidebar-logo-text" id="sidebar-logo-text">PREUVU</div>
          <div class="sidebar-logo-tagline">${isAdmin ? '⚙ Admin Console' : industryLabel(industry)}</div>
        </div>
        ${buildNavItems(!!session, isAdmin, industry)}
        ${session ? `<div style="margin-top:auto;padding:12px 16px;border-top:1px solid var(--border)"><button class="btn btn-ghost btn-sm" id="logout-btn" style="width:100%">Sign Out</button></div>` : ''}
      </nav>
      <main class="main-content" id="main-content"></main>
    </div>
    <div class="toast-container" id="toast-container"></div>
  `;

  mainContentEl = document.getElementById('main-content')!;

  // Mobile sidebar toggle
  const sidebarEl = document.getElementById('sidebar');
  const overlayEl = document.getElementById('sidebar-overlay');
  const openSidebar = (): void => {
    sidebarEl?.classList.add('mobile-open');
    overlayEl?.classList.add('active');
  };
  const closeSidebar = (): void => {
    sidebarEl?.classList.remove('mobile-open');
    overlayEl?.classList.remove('active');
  };
  document.getElementById('hamburger-btn')?.addEventListener('click', openSidebar);
  document.getElementById('sidebar-close-btn')?.addEventListener('click', closeSidebar);
  overlayEl?.addEventListener('click', closeSidebar);

  document.querySelectorAll('.nav-item[data-route]').forEach((el) => {
    el.addEventListener('click', () => {
      closeSidebar();
      navigate(el.getAttribute('data-route') ?? 'dashboard');
    });
  });

  document.getElementById('logout-btn')?.addEventListener('click', () => {
    clearSession();
    navigate(isWebMode() ? 'landing' : 'login');
  });

  renderRoute();
  updateActiveNav();

  // Apply saved logo to sidebar after shell is built
  const savedLogo = localStorage.getItem('preuve_logo') ?? '';
  if (savedLogo) applyLogoToSidebar(savedLogo);
}

function buildNavItems(isAuth: boolean, isAdmin: boolean, industry: string): string {
  if (!isAuth) return '';

  if (isAdmin) return `
    <div class="nav-section-label">Admin</div>
    <div class="nav-item" data-route="admin"><i class="bi bi-buildings"></i> Business Approvals</div>
    <div class="nav-section-label" style="margin-top:16px">Tools</div>
    <div class="nav-item" data-route="verify"><i class="bi bi-patch-check"></i> Verify Certificate</div>
    <div class="nav-item" data-route="settings"><i class="bi bi-gear"></i> Settings</div>
  `;

  const industryNav =
    industry === 'construction' ? `
      <div class="nav-section-label">Construction</div>
      <div class="nav-item" data-route="templates"><i class="bi bi-file-earmark-text"></i> Templates & Docs</div>
      <div class="nav-item" data-route="template-builder"><i class="bi bi-stars"></i> Build Template</div>
      <div class="nav-item" data-route="scanner"><i class="bi bi-camera2"></i> Document Scanner</div>
      <div class="nav-item" data-route="invoices"><i class="bi bi-receipt"></i> Invoices</div>
      <div class="nav-item" data-route="balance"><i class="bi bi-coin"></i> Project Financials</div>
      <div class="nav-item" data-route="payroll"><i class="bi bi-cash-stack"></i> Payroll</div>
      <div class="nav-item" data-route="customers"><i class="bi bi-people"></i> Customers</div>
      <div class="nav-item" data-route="employees"><i class="bi bi-person-badge"></i> Employees & PINs</div>
      <div class="nav-item" data-route="analytics"><i class="bi bi-graph-up"></i> Analytics</div>
      <div class="nav-item" data-route="compliance"><i class="bi bi-shield-check"></i> WA Compliance</div>
    ` : industry === 'restaurant' ? `
      <div class="nav-section-label">Restaurant</div>
      <div class="nav-item" data-route="restaurant"><i class="bi bi-menu-button-wide"></i> Menu Management</div>
      <div class="nav-item" data-route="tables"><i class="bi bi-grid"></i> Table Management</div>
      <div class="nav-item" data-route="restaurant-tablet"><i class="bi bi-tablet"></i> Tablet Ordering</div>
      <div class="nav-item" data-route="kitchen"><i class="bi bi-fire"></i> Kitchen Display</div>
      <div class="nav-item" data-route="schedule"><i class="bi bi-calendar3"></i> Work Schedule</div>
      <div class="nav-item" data-route="balance"><i class="bi bi-credit-card"></i> POS & Revenue</div>
      <div class="nav-item" data-route="payroll"><i class="bi bi-cash-stack"></i> Payroll</div>
      <div class="nav-item" data-route="customers"><i class="bi bi-people"></i> Customers</div>
      <div class="nav-item" data-route="employees"><i class="bi bi-person-badge"></i> Employees & Tips</div>
      <div class="nav-item" data-route="scanner"><i class="bi bi-camera2"></i> Document Scanner</div>
      <div class="nav-item" data-route="analytics"><i class="bi bi-graph-up"></i> Analytics</div>
      <div class="nav-item" data-route="compliance"><i class="bi bi-shield-check"></i> WA Compliance</div>
    ` : `
      <div class="nav-section-label">Documents</div>
      <div class="nav-item" data-route="templates"><i class="bi bi-file-earmark-text"></i> Template Library</div>
      <div class="nav-item" data-route="scanner"><i class="bi bi-camera2"></i> Document Scanner</div>
      <div class="nav-item" data-route="balance"><i class="bi bi-coin"></i> Financials</div>
      <div class="nav-item" data-route="payroll"><i class="bi bi-cash-stack"></i> Payroll</div>
      <div class="nav-item" data-route="customers"><i class="bi bi-people"></i> Customers</div>
      <div class="nav-item" data-route="employees"><i class="bi bi-person-badge"></i> Employees & PINs</div>
      <div class="nav-item" data-route="analytics"><i class="bi bi-graph-up"></i> Analytics</div>
      <div class="nav-item" data-route="compliance"><i class="bi bi-shield-check"></i> WA Compliance</div>
    `;

  return `
    <div class="nav-section-label">Workspace</div>
    <div class="nav-item" data-route="dashboard"><i class="bi bi-house"></i> Dashboard</div>
    <div class="nav-item" data-route="new-session"><i class="bi bi-stars"></i> AI Session</div>
    ${industryNav}
    <div class="nav-section-label" style="margin-top:16px">Tools</div>
    <div class="nav-item" data-route="verify"><i class="bi bi-patch-check"></i> Verify Certificate</div>
    <div class="nav-item" data-route="verify-identity"><i class="bi bi-person-vcard"></i> Identity & Docs</div>
    <div class="nav-item" data-route="billing"><i class="bi bi-credit-card"></i> Billing</div>
    <div class="nav-item" data-route="settings"><i class="bi bi-gear"></i> Settings</div>
  `;
}

function industryLabel(industry: string): string {
  return ({ construction: '🏗 Construction', restaurant: '🍽 Restaurant', general: '💼 General Business' } as Record<string, string>)[industry] ?? 'Business Platform';
}

function esc(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

document.addEventListener('DOMContentLoaded', () => {
  // Apply saved theme before first render
  const savedTheme = localStorage.getItem('preuve_theme') ?? 'midnight';
  applyTheme(savedTheme);

  // The Electron CSS locks body and #app to height:100vh / overflow:hidden so the
  // desktop app never shows OS scroll bars. On the public web build we need natural
  // document scrolling, so unlock those constraints immediately.
  if (isWebMode()) {
    document.documentElement.style.height = 'auto';
    document.body.style.height = 'auto';
    document.body.style.overflow = 'auto';
    document.body.style.userSelect = 'auto';
    const appEl = document.getElementById('app');
    if (appEl) { appEl.style.height = 'auto'; appEl.style.minHeight = '100vh'; }
  }

  const session = getSession();
  // Check if URL has a route param (e.g. ?route=verify&cert=XXX)
  const urlParams = new URLSearchParams(window.location.search);
  const routeParam = urlParams.get('route');
  if (routeParam) {
    currentRoute = routeParam;
    urlParams.delete('route');
    urlParams.forEach((v, k) => { currentParams[k] = v; });
  } else {
    currentRoute = session ? 'dashboard' : (isWebMode() ? 'landing' : 'login');
  }
  // Sync shellIsFullscreen with the initial route so buildShell() uses the right mode
  shellIsFullscreen = FULLSCREEN_ROUTES.has(currentRoute);
  (window as unknown as { preuveNavigate: typeof navigate }).preuveNavigate = navigate;
  buildShell();
});
